import { useEffect, useRef, useState } from "react";

/**
 * Reveal — scroll-triggered fade-in wrapper.
 * Uses IntersectionObserver, fires once, then unobserves.
 * Respects prefers-reduced-motion via the .reveal CSS class.
 *
 * Props:
 *   as        — element tag, defaults to "div"
 *   delay     — 0-4, mapped to reveal-delay-N class
 *   className — extra classes
 */
export default function Reveal({ as: Tag = "div", delay = 0, className = "", children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const delayClass = delay ? `reveal-delay-${Math.min(4, Math.max(1, delay))}` : "";
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "in" : ""} ${delayClass} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}
